import { Clock, AlertCircle, RefreshCw } from 'lucide-react'
import { cn, formatRelativeTime } from '@/lib/utils'
import { useCronHealth, type CronHealthRow, type CronStatus } from '@/hooks/useCronHealth'
import { StatusPill } from './shared/StatusPill'
import { RealTimeIndicator } from './shared/RealTimeIndicator'

const ERROR_TRUNCATE_CHARS = 80

function fmtDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '—'
  if (ms < 1000) return `${ms} ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`
  return `${(ms / 60_000).toFixed(1)} min`
}

function truncateError(msg: string | null): string {
  if (!msg) return ''
  if (msg.length <= ERROR_TRUNCATE_CHARS) return msg
  return msg.slice(0, ERROR_TRUNCATE_CHARS) + '…'
}

function rowAccentClass(status: CronStatus): string {
  if (status === 'last_run_failed') return 'border-l-2 border-l-status-error'
  if (status === 'stale') return 'border-l-2 border-l-status-warning'
  return ''
}

function mapError(error: unknown): string {
  if (!error) return 'Cron health is currently unavailable.'
  const message = error instanceof Error ? error.message : String(error)
  // PostgREST permission errors surface as 401/403 in message
  if (/permission|denied|401|403/i.test(message)) {
    return 'Missing or invalid credentials for cron health.'
  }
  if (/network|fetch|failed to fetch/i.test(message)) {
    return 'Unable to reach Supabase. Check connectivity.'
  }
  return 'Cron health is currently unavailable.'
}

// ────────────────────────────────────────────────────────────────────────
// Sub-views
// ────────────────────────────────────────────────────────────────────────

function CronRowDesktop({ row }: { row: CronHealthRow }) {
  return (
    <tr className={cn('border-b border-border last:border-0', rowAccentClass(row.status))}>
      <td className="px-3 py-2.5 text-sm font-mono">{row.function_name}</td>
      <td className="px-3 py-2.5">
        <StatusPill status={row.status} />
      </td>
      <td className="px-3 py-2.5 text-sm text-muted-foreground" title={row.last_success_at ?? ''}>
        {row.last_success_at ? `${formatRelativeTime(row.last_success_at)} ago` : '—'}
      </td>
      <td className="px-3 py-2.5 text-sm text-muted-foreground">
        {fmtDuration(row.last_duration_ms)}
      </td>
      <td className="px-3 py-2.5 text-sm text-muted-foreground max-w-xs">
        {row.last_error_message ? (
          <span title={row.last_error_message} className="text-status-error">
            {truncateError(row.last_error_message)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  )
}

function CronRowMobile({ row }: { row: CronHealthRow }) {
  const lastSuccess = row.last_success_at
    ? `${formatRelativeTime(row.last_success_at)} ago`
    : '—'
  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-3 shadow-sm space-y-1.5',
        rowAccentClass(row.status)
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        <StatusPill status={row.status} />
        <p className="text-sm font-mono font-medium truncate">{row.function_name}</p>
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span title={row.last_success_at ?? ''}>{lastSuccess}</span>
        <span>{fmtDuration(row.last_duration_ms)}</span>
      </div>
      {row.last_error_message && (
        <p
          className="text-xs text-status-error break-words"
          title={row.last_error_message}
        >
          {truncateError(row.last_error_message)}
        </p>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Main section
// ────────────────────────────────────────────────────────────────────────

export function CronHealthSection() {
  const { data, isLoading, isError, error, refetch, isFetching } = useCronHealth()

  return (
    <div>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
          <Clock className="h-5 w-5 text-brand-fg" />
          Cron Jobs
        </h3>
        <RealTimeIndicator />
      </div>

      {isLoading && (
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <div className="space-y-3">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="h-10 w-full bg-muted rounded animate-pulse" />
            ))}
          </div>
        </div>
      )}

      {isError && !isLoading && (
        <div className="rounded-lg border border-status-error/30 bg-status-error/10 p-4 sm:p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-status-error shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-status-error font-medium">{mapError(error)}</p>
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-status-error/30 text-xs text-status-error hover:bg-status-error/10 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {!isLoading && !isError && data && data.length === 0 && (
        <div className="rounded-lg border bg-card p-6 shadow-sm text-center text-sm text-muted-foreground">
          No cron jobs registered yet.
        </div>
      )}

      {!isLoading && !isError && data && data.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-lg border bg-card shadow-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Function
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Last success
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Last duration
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Last error
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map(row => (
                  <CronRowDesktop key={row.function_name} row={row} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {data.map(row => (
              <CronRowMobile key={row.function_name} row={row} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
